/**
 * The game-spec schema: the only door through which a game definition enters the
 * system.
 *
 * A spec is **data**, never code. Every field is a literal or a tag drawn from a
 * closed vocabulary, so a spec can be authored by a stranger, stored, and later
 * executed by `interpreter.ts` without any user-supplied logic ever running.
 * There is deliberately no expression language, no path reference from one part
 * of a spec to another, and no user-supplied regex.
 *
 * Validation and execution are separate phases: parse once at ingest, then
 * execute the parsed value forever after. `interpreter.ts` imports this module
 * with `import type` only, which keeps the correctness core dependency-free.
 *
 * The three sections are read by three disjoint consumers:
 *  - `rules`        — the interpreter, and nothing else;
 *  - `components`   — the print/export pipeline, and nothing else;
 *  - `presentation` — the digital client renderer, and nothing else.
 *
 * Engine signatures take `RulesSpec`, never `GameSpec`, so it is structurally
 * impossible for the interpreter to read a colour.
 */
import { z } from 'zod';

/* ------------------------------------------------------------------------- *
 * Size caps. Specs are untrusted input, so every dimension is bounded before
 * anything is stored. The interpreter additionally clamps its own loops, since
 * these refinements are invisible to the type system.
 * ------------------------------------------------------------------------- */

/** Largest board edge. */
export const MAX_BOARD_EDGE = 32;
/**
 * Largest board area, which bounds every interpreter loop. Enforced by
 * construction rather than by a check: `MAX_BOARD_EDGE ** 2 === 1024`, so the
 * per-edge caps already make a larger area unrepresentable.
 */
export const MAX_BOARD_CELLS = MAX_BOARD_EDGE * MAX_BOARD_EDGE;
export const MAX_SEATS = 8;
/** Seats are single digits because `encoding: 'digits'` is one char per cell. */
export const MAX_SEAT_VALUE = 9;
export const MAX_DIRECTIONS = 8;
export const MAX_MOVE_DEFS = 4;
export const MAX_TERMINAL_CONDITIONS = 4;
export const MAX_PIECE_KINDS = 64;
export const MAX_ID_LENGTH = 64;
export const MAX_LABEL_LENGTH = 256;
export const MAX_PROSE_LENGTH = 4096;

const IdSchema = z.string().min(1).max(MAX_ID_LENGTH);
const LabelSchema = z.string().min(1).max(MAX_LABEL_LENGTH);
const ProseSchema = z.string().min(1).max(MAX_PROSE_LENGTH);
/** Constant pattern, never spec-supplied: no ReDoS surface. */
const HexColorSchema = z.string().regex(/^#[0-9a-f]{6}$/);
const MillimetresSchema = z.number().positive().max(2000);
const SeatValueSchema = z.number().int().min(1).max(MAX_SEAT_VALUE);
const EdgeSchema = z.number().int().min(2).max(MAX_BOARD_EDGE);

/* ------------------------------------------------------------------------- *
 * rules — the only section the interpreter sees.
 * ------------------------------------------------------------------------- */

/**
 * A single scan step as `[dRow, dCol]`. Steps are capped at one cell so a line
 * is a genuine line; the negations are covered by scanning both ways, so
 * `[0,1]` and `[0,-1]` are the same direction and are rejected as duplicates.
 */
const DirectionSchema = z
  .tuple([z.number().int().min(-1).max(1), z.number().int().min(-1).max(1)])
  .readonly();

const BoardSpecSchema = z.strictObject({
  kind: z.literal('grid'),
  cols: EdgeSchema,
  rows: EdgeSchema,
  /** `index = row * cols + col`. */
  index: z.literal('row_major'),
  /** Orientation fact. Cross-checked against `presentation.layout.renderRowZeroAt`. */
  rowZero: z.enum(['bottom', 'top']),
});

const CellsSpecSchema = z.strictObject({
  empty: z.literal(0),
  /** A cell holds `empty` or a seat number. */
  occupancy: z.literal('seat'),
  /**
   * `write_once` licenses `through: 'last_placement'`: when pieces are never
   * removed or moved, scanning only the played cell is provably equivalent to a
   * full board scan. Milestone one executes `write_once` only.
   */
  mutation: z.enum(['write_once', 'mutable']),
});

const MoveSpecSchema = z.strictObject({
  id: IdSchema,
  kind: z.literal('drop'),
  /** The move's single numeric parameter is a column index in `[0, cols)`. */
  param: z.literal('column'),
  /** The landing cell is searched within one column. */
  along: z.literal('column'),
  /** Gravity: `ascending` fills from row 0 upward, `descending` from the top down. */
  fill: z.enum(['ascending', 'descending']),
});

const WinConditionSchema = z.strictObject({
  kind: z.literal('line'),
  length: z.number().int().min(2).max(MAX_BOARD_EDGE),
  /** Licensed by `cells.mutation === 'write_once'`; see `CellsSpecSchema`. */
  through: z.literal('last_placement'),
  directions: z.array(DirectionSchema).min(1).max(MAX_DIRECTIONS).readonly(),
});

const DrawConditionSchema = z.strictObject({ kind: z.literal('board_full') });

/**
 * `moves` is a tuple-with-rest so the first definition is statically present:
 * under `noUncheckedIndexedAccess` an array would make `moves[0]` optional and
 * force an unreachable branch into the interpreter.
 */
export const RulesSpecSchema = z
  .strictObject({
    /** Seat identity **and** rotation order. */
    seats: z.tuple([SeatValueSchema, SeatValueSchema], SeatValueSchema).readonly(),
    turnOrder: z.strictObject({ kind: z.literal('cyclic') }),
    board: BoardSpecSchema,
    cells: CellsSpecSchema,
    moves: z.tuple([MoveSpecSchema], MoveSpecSchema).readonly(),
    terminal: z.strictObject({
      /** Evaluated before `draw`, in declared order; the first hit wins. */
      win: z.tuple([WinConditionSchema], WinConditionSchema).readonly(),
      draw: z.array(DrawConditionSchema).max(MAX_TERMINAL_CONDITIONS).readonly(),
    }),
    /** One char per cell: `'0' + seat digits`. Load-bearing for logs and replay. */
    encoding: z.strictObject({ kind: z.literal('digits') }),
  })
  .superRefine((rules, ctx) => {
    const { cols, rows } = rules.board;

    if (rules.seats.length > MAX_SEATS) {
      ctx.addIssue({
        code: 'custom',
        path: ['seats'],
        message: `at most ${MAX_SEATS} seats, got ${rules.seats.length}`,
      });
    }
    if (new Set(rules.seats).size !== rules.seats.length) {
      ctx.addIssue({ code: 'custom', path: ['seats'], message: 'seat numbers must be unique' });
    }

    if (rules.moves.length > MAX_MOVE_DEFS) {
      ctx.addIssue({
        code: 'custom',
        path: ['moves'],
        message: `at most ${MAX_MOVE_DEFS} move definitions, got ${rules.moves.length}`,
      });
    }

    if (rules.terminal.win.length > MAX_TERMINAL_CONDITIONS) {
      ctx.addIssue({
        code: 'custom',
        path: ['terminal', 'win'],
        message: `at most ${MAX_TERMINAL_CONDITIONS} win conditions, got ${rules.terminal.win.length}`,
      });
    }

    const longestEdge = Math.max(cols, rows);
    for (const [conditionIndex, condition] of rules.terminal.win.entries()) {
      const at = ['terminal', 'win', conditionIndex];

      // `last_placement` is only sound when cells are never rewritten.
      if (rules.cells.mutation !== 'write_once') {
        ctx.addIssue({
          code: 'custom',
          path: at,
          message: "through: 'last_placement' requires cells.mutation: 'write_once'",
        });
      }

      if (condition.length > longestEdge) {
        ctx.addIssue({
          code: 'custom',
          path: [...at, 'length'],
          message: `win length ${condition.length} cannot fit a ${cols}x${rows} board`,
        });
      }

      const seen = new Set<string>();
      for (const [directionIndex, direction] of condition.directions.entries()) {
        const path = [...at, 'directions', directionIndex];
        const [dRow, dCol] = direction;
        if (dRow === 0 && dCol === 0) {
          ctx.addIssue({ code: 'custom', path, message: 'direction must be non-zero' });
          continue;
        }
        const canonical = canonicalDirection(dRow, dCol);
        if (seen.has(canonical)) {
          ctx.addIssue({
            code: 'custom',
            path,
            message: `direction [${dRow}, ${dCol}] duplicates an earlier direction`,
          });
        }
        seen.add(canonical);
      }
    }
  });

/**
 * A direction and its negation scan the same cells, so they collapse to one key.
 * Flip whichever representative has a negative leading component.
 */
function canonicalDirection(dRow: number, dCol: number): string {
  const flip = dRow < 0 || (dRow === 0 && dCol < 0);
  return flip ? `${-dRow},${-dCol}` : `${dRow},${dCol}`;
}

/* ------------------------------------------------------------------------- *
 * components — print/export only, and self-sufficient.
 * ------------------------------------------------------------------------- */

/**
 * Counts and dimensions are **declared, never derived**. The pipeline must not
 * compute a piece count from `cols * rows / seats`, because that would make the
 * printer depend on rules semantics. The duplication is proved coherent at
 * ingest time by `GameSpecSchema`'s cross-checks instead.
 *
 * Law: if a physical quantity ever constrains legality (limited supply games),
 * it must be restated inside `rules` as its own field. `components` is never
 * read at runtime, by anything, ever.
 */
export const ComponentsSpecSchema = z.strictObject({
  title: LabelSchema,
  playerCountLabel: LabelSchema,
  board: z.strictObject({
    grid: z.strictObject({ cols: EdgeSchema, rows: EdgeSchema }),
    cellShape: z.enum(['circle', 'square']),
    cellDiameterMm: MillimetresSchema,
    cellSpacingMm: MillimetresSchema,
    frameThicknessMm: MillimetresSchema,
    sheetSizeMm: z.strictObject({ w: MillimetresSchema, h: MillimetresSchema }),
    bleedMm: z.number().min(0).max(50),
    material: z.enum(['card', 'wood', 'acrylic']),
  }),
  pieces: z
    .array(
      z.strictObject({
        id: IdSchema,
        seatRef: SeatValueSchema,
        label: LabelSchema,
        count: z.number().int().min(1).max(MAX_BOARD_CELLS),
        shape: z.enum(['disc', 'cube', 'pawn']),
        diameterMm: MillimetresSchema,
        thicknessMm: MillimetresSchema,
        colorHex: HexColorSchema,
      }),
    )
    .min(1)
    .max(MAX_PIECE_KINDS)
    .readonly(),
  /** Human prose for the printed sheet. Never parsed. */
  rulesSummaryText: ProseSchema,
});

/* ------------------------------------------------------------------------- *
 * presentation — digital client only.
 * ------------------------------------------------------------------------- */

/** The closed set of placeholders a label template may use. Substitution only. */
export const CELL_TEMPLATE_PLACEHOLDERS = ['row', 'col', 'occupant'] as const;
const PLACEHOLDER_PATTERN = /\{[a-z]+\}/g;

const CellTemplateSchema = z
  .string()
  .min(1)
  .max(MAX_LABEL_LENGTH)
  .superRefine((template, ctx) => {
    for (const match of template.matchAll(PLACEHOLDER_PATTERN)) {
      const name = match[0].slice(1, -1);
      if (!(CELL_TEMPLATE_PLACEHOLDERS as readonly string[]).includes(name)) {
        ctx.addIssue({ code: 'custom', message: `unknown placeholder '{${name}}'` });
      }
    }
  });

export const PresentationSpecSchema = z.strictObject({
  theme: z.strictObject({
    canvas: HexColorSchema,
    board: HexColorSchema,
    /** Keyed by seat number as a string. `marker` is the non-colour channel (WCAG 1.4.1). */
    seats: z.record(
      z.string(),
      z.strictObject({ color: HexColorSchema, marker: z.enum(['filled', 'ring']) }),
    ),
  }),
  layout: z.strictObject({
    renderRowZeroAt: z.enum(['bottom', 'top']),
    interactiveUnit: z.enum(['column', 'cell']),
    landingPreview: z.enum(['ghost', 'none']),
  }),
  motion: z.strictObject({
    dropMs: z.number().int().min(0).max(5000),
    easing: z.enum(['gravity', 'linear', 'ease-out']),
    respectsReducedMotion: z.boolean(),
  }),
  labels: z.strictObject({
    cellTemplate: CellTemplateSchema,
    occupant: z.strictObject({ empty: LabelSchema, own: LabelSchema, other: LabelSchema }),
    statuses: z.strictObject({ yourTurn: LabelSchema, theirTurn: LabelSchema }),
  }),
});

/* ------------------------------------------------------------------------- *
 * The whole spec, plus the cross-section coherence proofs.
 * ------------------------------------------------------------------------- */

export const GameSpecSchema = z
  .strictObject({
    id: IdSchema,
    specVersion: z.literal(1),
    rules: RulesSpecSchema,
    components: ComponentsSpecSchema,
    presentation: PresentationSpecSchema,
  })
  .superRefine((spec, ctx) => {
    const { rules, components, presentation } = spec;
    const { cols, rows } = rules.board;

    const grid = components.board.grid;
    if (grid.cols !== cols || grid.rows !== rows) {
      ctx.addIssue({
        code: 'custom',
        path: ['components', 'board', 'grid'],
        message: `printed grid ${grid.cols}x${grid.rows} does not match rules board ${cols}x${rows}`,
      });
    }

    const perSeat = new Map<number, number>();
    for (const [pieceIndex, piece] of components.pieces.entries()) {
      if (!rules.seats.includes(piece.seatRef)) {
        ctx.addIssue({
          code: 'custom',
          path: ['components', 'pieces', pieceIndex, 'seatRef'],
          message: `seat ${piece.seatRef} is not one of rules.seats`,
        });
        continue;
      }
      perSeat.set(piece.seatRef, (perSeat.get(piece.seatRef) ?? 0) + piece.count);
    }

    // The physical game must be able to reach the digital game's terminal state.
    const required = Math.ceil((cols * rows) / rules.seats.length);
    for (const seat of rules.seats) {
      const supply = perSeat.get(seat) ?? 0;
      if (supply < required) {
        ctx.addIssue({
          code: 'custom',
          path: ['components', 'pieces'],
          message: `seat ${seat} has ${supply} pieces but needs at least ${required}`,
        });
      }
    }

    if (presentation.layout.renderRowZeroAt !== rules.board.rowZero) {
      ctx.addIssue({
        code: 'custom',
        path: ['presentation', 'layout', 'renderRowZeroAt'],
        message: `renderRowZeroAt '${presentation.layout.renderRowZeroAt}' contradicts rules.board.rowZero '${rules.board.rowZero}'`,
      });
    }

    for (const seat of rules.seats) {
      if (presentation.theme.seats[String(seat)] === undefined) {
        ctx.addIssue({
          code: 'custom',
          path: ['presentation', 'theme', 'seats'],
          message: `seat ${seat} has no theme entry`,
        });
      }
    }
  });

export type RulesSpec = z.infer<typeof RulesSpecSchema>;
export type ComponentsSpec = z.infer<typeof ComponentsSpecSchema>;
export type PresentationSpec = z.infer<typeof PresentationSpecSchema>;
export type GameSpec = z.infer<typeof GameSpecSchema>;

export interface SpecIssue {
  readonly path: string;
  readonly message: string;
}

export type ParseSpecResult =
  | { readonly ok: true; readonly spec: GameSpec }
  | { readonly ok: false; readonly issues: readonly SpecIssue[] };

/** Total: returns a result for every input and never throws. */
export function parseGameSpec(input: unknown): ParseSpecResult {
  const result = GameSpecSchema.safeParse(input);
  if (result.success) return { ok: true, spec: result.data };
  return {
    ok: false,
    issues: result.error.issues.map((issue) => ({
      path: issue.path.map((segment) => String(segment)).join('.'),
      message: issue.message,
    })),
  };
}

/**
 * Print inks and screen tokens legitimately diverge, so a mismatch is a note
 * rather than a rejection. Reported here so authors can see the drift on
 * purpose instead of by accident.
 */
export function describeColorDrift(spec: GameSpec): readonly string[] {
  const notes: string[] = [];
  for (const piece of spec.components.pieces) {
    const theme = spec.presentation.theme.seats[String(piece.seatRef)];
    if (theme !== undefined && theme.color !== piece.colorHex) {
      notes.push(
        `seat ${piece.seatRef}: piece '${piece.id}' prints ${piece.colorHex} but renders ${theme.color}`,
      );
    }
  }
  return notes;
}
